import { QRCodeSVG } from "qrcode.react";
import { FaSyncAlt, FaUnlink } from "react-icons/fa";
import { formatNumber } from "../../../../Hooks/FormatNumber.hook";
import { PendingQrCode } from "../../../../interfaces/Sockets.interfaces";
import { WhatsappSession, WhatsappSessionAction } from "../../../../interfaces/WhatsappSessions.interfaces";
import {
    formatDateTime,
    formatDuration,
    formatElapsed,
    formatTime,
    getSessionState,
    STATE_LABELS,
} from "../sessionFormat";
import { StyledSessionCard } from "./style";

interface SessionCardProps {
    session: WhatsappSession;
    now: number;
    pendingAction?: WhatsappSessionAction;
    socketQr?: PendingQrCode;
    onRestart: () => void;
    onLogout: () => void;
};

const ACTION_LABELS: Record<WhatsappSessionAction, string> = {
    RESTART: "Reiniciando…",
    LOGOUT: "Desvinculando…",
};

export const SessionCard = ({ session, now, pendingAction, socketQr, onRestart, onLogout }: SessionCardProps) => {
    // Older wa-client-lite versions only announce the QR over the socket, without session fields.
    const state = !session.state && socketQr && !session.ready ? "QR_PENDING" : getSessionState(session);
    const qr = socketQr && state !== "CONNECTED"
        ? { value: socketQr.qr, at: new Date(socketQr.receivedAt).toISOString() }
        : state === "QR_PENDING" && session.qr
            ? { value: session.qr, at: session.qrAt }
            : null;
    const runningAction = pendingAction || session.action || null;
    const phone = session.phone ? formatNumber(session.phone) : null;
    const title = phone || formatNumber(session.number);

    const details = [
        session.pushName,
        phone && session.phone !== session.number ? `Instância ${session.number}` : null,
        session.type,
    ].filter(Boolean);

    return (
        <StyledSessionCard className={`state-${state}`}>
            <header>
                <div className="identity">
                    <h3>{title}</h3>
                    {details.length > 0 && <p>{details.join(" · ")}</p>}
                </div>
                <div className="status" role="status">
                    <span />
                    {runningAction ? ACTION_LABELS[runningAction] : STATE_LABELS[state]}
                </div>
            </header>

            {qr && (
                <div className="qr">
                    <div role="img" aria-label="QR code de pareamento">
                        <QRCodeSVG value={qr.value} size={168} />
                    </div>
                    <p>
                        No celular, abra o WhatsApp em <b>Dispositivos conectados</b> e leia este QR code.
                        <small>Gerado às {formatTime(qr.at)}; ele é renovado automaticamente.</small>
                    </p>
                </div>
            )}

            {session.state && (
                <dl>
                    <div>
                        <dt>Número conectado</dt>
                        <dd>
                            {phone || "—"}
                            {!phone && state !== "CONNECTED" && <small>Nenhum aparelho pareado</small>}
                        </dd>
                    </div>
                    <div>
                        <dt>Tempo de sessão</dt>
                        <dd>
                            {session.connectedAt ? formatDuration(now - new Date(session.connectedAt).getTime()) : "—"}
                            <small>
                                {session.connectedAt
                                    ? `desde ${formatDateTime(session.connectedAt)}`
                                    : "sem conexão ativa"}
                            </small>
                        </dd>
                    </div>
                    <div>
                        <dt>Última mensagem recebida</dt>
                        <dd>
                            {session.lastReceivedAt ? formatElapsed(session.lastReceivedAt, now) : "—"}
                            <small>
                                {session.lastReceivedAt ? formatDateTime(session.lastReceivedAt) : "nenhuma registrada"}
                            </small>
                        </dd>
                    </div>
                    <div>
                        <dt>Última mensagem enviada</dt>
                        <dd>
                            {session.lastSentAt ? formatElapsed(session.lastSentAt, now) : "—"}
                            <small>
                                {session.lastSentAt
                                    ? formatDateTime(session.lastSentAt)
                                    : "nenhuma desde o início do serviço"}
                            </small>
                        </dd>
                    </div>
                    <div>
                        <dt>Última atualização</dt>
                        <dd>
                            {session.stateChangedAt ? formatElapsed(session.stateChangedAt, now) : "—"}
                            <small>
                                {session.stateChangedAt
                                    ? `${STATE_LABELS[state].toLowerCase()} em ${formatDateTime(session.stateChangedAt)}`
                                    : "sem registro"}
                            </small>
                        </dd>
                    </div>
                    <div>
                        <dt>Serviço iniciado</dt>
                        <dd>
                            {session.startedAt ? formatElapsed(session.startedAt, now) : "—"}
                            <small>{formatDateTime(session.startedAt)}</small>
                        </dd>
                    </div>
                </dl>
            )}

            {state === "DISCONNECTED" && session.lastDisconnectReason && (
                <p className="reason">Motivo da desconexão: {session.lastDisconnectReason}</p>
            )}

            <footer>
                {session.manageable ? (
                    <>
                        <button type="button" onClick={onRestart} disabled={!!runningAction}>
                            <FaSyncAlt />
                            Reiniciar sessão
                        </button>
                        <button type="button" className="danger" onClick={onLogout} disabled={!!runningAction}>
                            <FaUnlink />
                            Desvincular número
                        </button>
                    </>
                ) : (
                    <p>
                        {session.type === "WWEBJS"
                            ? "Instâncias WWEBJS não são gerenciadas por este painel."
                            : "Atualize o wa-client-lite deste cliente para gerenciar a sessão por aqui."}
                    </p>
                )}
            </footer>
        </StyledSessionCard>
    );
};
